import { useState } from 'react'
import {
  PlayCircle, PauseCircle, Trash2, ChevronDown, ChevronRight,
  CircleDot, CheckCircle2, AlertCircle, Clock, Layers,
} from 'lucide-react'
import { useConfirm } from '@/components/common/ConfirmDialog'

// A backend-agnostic plan summary the panel can render. Both the AutoAgent WS
// `plans` event and the cross-backend REST list map onto this shape.
export interface PlanItem {
  id: string
  title: string
  kind: string
  status: 'running' | 'paused' | 'awaiting_review' | 'completed' | 'error' | 'setup'
  n_done?: number
  n_total?: number
  backend?: string
  updated_at?: number
  depends_on?: string[]
}

interface PlansPanelProps {
  plans: PlanItem[]
  activePlanId: string | null
  onSelect?: (id: string) => void
  onResume?: (id: string) => void
  // ``force`` (second press) stops a wedged run outright instead of waiting
  // for a checkpoint the run may never reach.
  onPause?: (id: string, force?: boolean) => void
  onDelete?: (id: string) => void
  // Disable resume buttons while another plan is running (sequential execution).
  busy?: boolean
  // Completed plans are hidden unless this is true (toggled from the dock's
  // 3-dot header menu). Keeps the dock uncluttered once work is finished.
  showCompleted?: boolean
}

// Theme tokens, so the colours follow the dark / light theme.
const STATUS_COLOR: Record<string, string> = {
  running: 'var(--color-accent)',
  paused: 'var(--color-warning)',
  awaiting_review: 'var(--color-warning)',
  completed: 'var(--color-success)',
  error: 'var(--color-error)',
  setup: 'var(--color-text-secondary)',
}

const STATUS_LABEL: Record<string, string> = {
  running: 'Running',
  paused: 'Paused',
  awaiting_review: 'Waiting for review',
  completed: 'Done',
  error: 'Error',
  setup: 'New',
}

function StatusIcon({ status }: { status: string }) {
  const c = STATUS_COLOR[status] || 'var(--color-text-secondary)'
  if (status === 'running') return <CircleDot size={16} color={c} aria-hidden="true" />
  if (status === 'completed') return <CheckCircle2 size={16} color={c} aria-hidden="true" />
  if (status === 'error') return <AlertCircle size={16} color={c} aria-hidden="true" />
  return <Clock size={16} color={c} aria-hidden="true" />
}

export default function PlansPanel({
  plans, activePlanId, onSelect, onResume, onPause, onDelete, busy = false,
  showCompleted = false,
}: PlansPanelProps) {
  // Collapsed by default: the conversation leads; the plan list is one click away.
  const [open, setOpen] = useState(false)
  // Plans we already asked to pause once — a second press escalates to a hard
  // stop, so a run stuck between checkpoints can't trap the plan as "running".
  const [pauseAsked, setPauseAsked] = useState<Set<string>>(new Set())
  const { confirm, confirmDialog } = useConfirm()
  if (!plans || plans.length === 0) return null

  // Hide finished plans unless the user opted to see them — once a plan is
  // DONE it just accumulates noise in the dock.
  const visible = showCompleted ? plans : plans.filter((p) => p.status !== 'completed')
  if (visible.length === 0) return null

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 0', background: 'transparent', border: 'none',
          cursor: 'pointer', color: 'var(--color-text-secondary)',
          fontSize: '1rem', fontWeight: 500, fontFamily: 'inherit',
        }}
      >
        {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        <Layers size={15} aria-hidden="true" />
        <span>{visible.length === 1 ? '1 plan' : `${visible.length} plans`}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
          {visible.map((p) => {
            const isActive = p.id === activePlanId
            const color = STATUS_COLOR[p.status] || 'var(--color-text-secondary)'
            const canResume = p.status === 'paused' || p.status === 'awaiting_review' || p.status === 'error'
            const canPause = p.status === 'running'
            const total = p.n_total ?? 0
            return (
              <div
                key={p.id}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--color-surface-2)' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 6px 8px 10px', borderRadius: 8,
                  // A thin status-coloured rail on the active row replaces the
                  // heavy per-row boxes — far less visual clutter.
                  borderLeft: isActive ? `2px solid ${color}` : '2px solid transparent',
                  background: isActive ? 'var(--color-surface-2)' : 'transparent',
                  transition: 'background 120ms ease',
                }}
              >
                <button
                  onClick={() => onSelect?.(p.id)}
                  title="View this plan"
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                    gap: 2, background: 'transparent', border: 'none',
                    cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StatusIcon status={p.status} />
                    <span
                      style={{
                        fontSize: '1rem', fontWeight: 500, color: 'var(--color-text)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {p.title || 'Untitled plan'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9375rem', color: 'var(--color-text-secondary)', paddingLeft: 24 }}>
                    <span style={{ color, fontWeight: 600 }}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                    {total > 0 && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{p.n_done ?? 0} of {total} done</span>
                      </>
                    )}
                  </div>
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {canResume && onResume && (
                    <button
                      onClick={() => onResume(p.id)}
                      disabled={busy}
                      title={busy ? 'Another plan is running' : 'Resume this plan'}
                      style={iconBtn(busy)}
                    >
                      <PlayCircle size={18} />
                    </button>
                  )}
                  {canPause && onPause && (
                    <button
                      onClick={() => {
                        const asked = pauseAsked.has(p.id)
                        onPause(p.id, asked)
                        if (!asked) {
                          setPauseAsked((prev) => new Set(prev).add(p.id))
                        }
                      }}
                      title={pauseAsked.has(p.id)
                        ? 'Stop this plan now'
                        : 'Pause this plan at the next checkpoint'}
                      style={iconBtn(false)}
                    >
                      <PauseCircle size={18} />
                    </button>
                  )}
                  {/* Deletable in EVERY state, running included. A run that
                      died mid-step leaves the plan stamped "running" forever;
                      hiding delete there left the user with no way out of a
                      plan that every later message got pulled back into. A
                      running plan just gets a blunter confirmation. */}
                  {onDelete && (
                    <button
                      onClick={async () => {
                        const stuck = p.status === 'running' || p.status === 'error'
                        const ok = await confirm({
                          title: 'Delete plan',
                          message: (
                            <>
                              <div style={{ marginBottom: stuck ? 8 : 0 }}>
                                Delete <strong>{p.title || 'this plan'}</strong>? This
                                can&apos;t be undone.
                              </div>
                              {p.status === 'running' && (
                                <div>
                                  It is still marked as running — deleting it stops any
                                  work in progress.
                                </div>
                              )}
                              {p.status === 'error' && (
                                <div>
                                  It ended in an error. Deleting it clears it out of the
                                  way so new requests aren&apos;t pulled back into it.
                                </div>
                              )}
                            </>
                          ),
                          confirmLabel: 'Delete plan',
                        })
                        if (ok) onDelete(p.id)
                      }}
                      title="Delete this plan"
                      style={iconBtn(false)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {confirmDialog}
    </div>
  )
}

function iconBtn(disabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 34, height: 34, borderRadius: 8, border: 'none',
    background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
    color: 'var(--color-text-secondary)',
    opacity: disabled ? 0.45 : 1,
  }
}
